import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Select, Space, Tag, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    BaseItem as ZhBaseItem,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { SORT } from "@/meta/common";
import ShipMonitorModal, {
    type ShipMonitorDevice,
    type ShipMonitorMapShip
} from "@/views/pages/logistics/shipManagement/ShipMonitorModal";
import { shipService } from "@/api/services/shipService";
import { useUserStore } from "@/views/stores";
import { PARTNERSHIP, TRANSPORT_CAPACITY_STATE } from "@zhihaoscm/meta";
import { TransportCapacityReview } from "./TransportCapacityReview";

export interface TransportCapacityDevice {
    name?: string;
    online?: number;
    createdTime?: string;
}

export interface TransportCapacityRow {
    ownerShip: { id?: string; state?: number; partnership?: number; createdTime?: string; rejectReason?: string };
    ship?: {
        id?: string;
        name?: string;
        cnname?: string;
        type?: number;
        deep?: number;
        imo?: string;
        callSign?: string;
        length?: number;
        width?: number;
        draft?: number;
    };
    owner?: { institutionName?: string };
    device?: TransportCapacityDevice | TransportCapacityDevice[];
    captain?: { realName?: string };
}
export interface TransportCapacityMonitorShip {
    ship?: Partial<ShipMonitorMapShip>;
    devices?: ShipMonitorDevice[];
}
export interface TransportCapacityQuery {
    page: number;
    size: number;
    shipKeywords?: string;
    ownerKeywords?: string;
    state?: number;
    partnership?: number;
    sortKey?: string;
    sortOrder?: string;
}
export interface TransportCapacityManagementPageProps {
    loadCapacity?: (query: TransportCapacityQuery) => Promise<{ list?: TransportCapacityRow[]; total?: number }>;
    loadMonitorShip?: (id: string) => Promise<TransportCapacityMonitorShip>;
    auditCapacity?: (id: string, payload: { state: number; rejectReason?: string }) => Promise<unknown>;
    validateAudit?: (id: string) => Promise<{ resultType?: number }>;
    unbindCapacity?: (id: string) => Promise<unknown>;
    canManage?: boolean;
    banButton?: boolean;
}

/** 复用 Vue owner-ship 分页接口。 */
export async function loadTransportCapacity(query: TransportCapacityQuery) {
    const response = await shippingRequestHttp.service.get("/owner-ship/paging", { params: query });
    return response.data;
}
/** 复用 Vue 解绑接口。 */
export async function unbindTransportCapacity(id: string) {
    const response = await shippingRequestHttp.service.put(`/owner-ship/unbind/${id}`);
    return response.data;
}
/** 复用旧 Vue 监控入口的船舶 VO 查询，用返回的 devices 初始化监控弹窗。 */
export async function loadTransportCapacityMonitorShip(id: string) {
    return (await shipService.getInfo(id)) as unknown as TransportCapacityMonitorShip;
}
const text = (value?: string) => value || "--";
const dateText = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : "--");
/** 旧 Vue 列表允许设备字段返回对象或数组，统一取首个设备用于展示。 */
const firstDevice = (device?: TransportCapacityDevice | TransportCapacityDevice[]) =>
    Array.isArray(device) ? device[0] : device;

/** 将旧 Vue 运力表的设备创建时间排序转换为分页接口参数。 */
export function buildTransportCapacitySortQuery(
    query: TransportCapacityQuery,
    sort: { prop?: string; order: "ascending" | "descending" | null }
): TransportCapacityQuery {
    const nextQuery = { ...query, page: 1 };
    delete nextQuery.sortKey;
    delete nextQuery.sortOrder;
    if (sort.prop === "device.createdTime" && sort.order) {
        nextQuery.sortKey = "created_time";
        nextQuery.sortOrder = SORT.getIntroByCode(sort.order);
    }
    return nextQuery;
}

/** 物流管理-运力管理列表，保持 Vue 筛选字段、状态操作和分页请求。 */
export function TransportCapacityManagementPage({
    loadCapacity = loadTransportCapacity,
    loadMonitorShip = loadTransportCapacityMonitorShip,
    auditCapacity,
    validateAudit,
    unbindCapacity = unbindTransportCapacity,
    canManage,
    banButton
}: TransportCapacityManagementPageProps) {
    const storeCanManage = useUserStore(state => state.hasAnyPermission(["ROLE_CM_M"]));
    // 复用旧 Vue 全局按钮封禁状态，监控入口也必须遵循该状态。
    const storeBanButton = useUserStore(state => state.banButton);
    const isButtonBanned = banButton ?? storeBanButton;
    const mayManage = canManage ?? storeCanManage;
    const [rows, setRows] = useState<TransportCapacityRow[]>([]);
    const [total, setTotal] = useState(0);
    // 对齐旧 Vue useTable 的默认分页大小，保持首屏、查询和重置请求契约一致。
    const [query, setQuery] = useState<TransportCapacityQuery>({ page: 1, size: 10 });
    const [shipKeywords, setShipKeywords] = useState("");
    const [ownerKeywords, setOwnerKeywords] = useState("");
    const [state, setState] = useState<number>();
    const [partnership, setPartnership] = useState<number>();
    const [errorMessage, setErrorMessage] = useState("");
    const [unbindRow, setUnbindRow] = useState<TransportCapacityRow>();
    const [auditRow, setAuditRow] = useState<TransportCapacityRow>();
    const [monitorOpen, setMonitorOpen] = useState(false);
    const [monitorShip, setMonitorShip] = useState<TransportCapacityMonitorShip>();
    const [monitorDevice, setMonitorDevice] = useState<ShipMonitorDevice>();
    const [monitorShipId, setMonitorShipId] = useState<string>();

    useEffect(() => {
        let disposed = false;
        void loadCapacity(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("运力列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCapacity, query]);
    const submit = () =>
        setQuery({
            page: 1,
            size: 10,
            ...(shipKeywords.trim() ? { shipKeywords: shipKeywords.trim() } : {}),
            ...(ownerKeywords.trim() ? { ownerKeywords: ownerKeywords.trim() } : {}),
            ...(state === undefined ? {} : { state }),
            ...(partnership === undefined ? {} : { partnership })
        });
    const refresh = () => setQuery(current => ({ ...current }));
    const label = (value?: number) => TRANSPORT_CAPACITY_STATE.getIntroByCode(value) || "--";
    const handleSortChange = (sort: { prop?: string; order: "ascending" | "descending" | null }) =>
        setQuery(current => buildTransportCapacitySortQuery(current, sort));

    /** 按旧 Vue 列表 openMonitor 契约加载船舶 VO，并打开现有 React 监控弹窗。 */
    const handleMonitor = async (row: TransportCapacityRow) => {
        const shipId = row.ship?.id;
        if (!shipId) {
            setErrorMessage("监控加载失败");
            return;
        }
        try {
            const result = await loadMonitorShip(String(shipId));
            setMonitorShip(result);
            setMonitorDevice(result.devices?.[0]);
            setMonitorShipId(String(result.ship?.id ?? shipId));
            setMonitorOpen(true);
            setErrorMessage("");
        } catch {
            setErrorMessage("监控加载失败");
        }
    };

    // 复用 Vue 解绑确认逻辑，成功后关闭弹窗并刷新当前运力列表。
    const confirmUnbind = async () => {
        if (!unbindRow?.ownerShip.id) return;
        try {
            await unbindCapacity(unbindRow.ownerShip.id);
            message.success("解绑成功");
            setUnbindRow(undefined);
            refresh();
        } catch {
            setErrorMessage("解绑失败");
        }
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue 运力管理 ZhTable 的字段和 operation 插槽。
    const capacityColumns = useMemo<TableColumnProps<TransportCapacityRow>[]>(
        () => [
            {
                key: "mmsi",
                label: "MMSI",
                width: 106,
                prop: "ship.id",
                render: ({ row }) => (
                    <Link to={`/logistics/transportCapacityManagement/capacityManagement/detail/${row.ownerShip.id}`}>
                        {text(row.ship?.id)}
                    </Link>
                )
            },
            {
                key: "ship",
                label: "船舶名称",
                minWidth: 176,
                prop: "ship.name",
                render: ({ row }) => `${text(row.ship?.cnname)}/${text(row.ship?.name)}`
            },
            {
                key: "owner",
                label: "货主",
                minWidth: 148,
                prop: "owner.institutionName",
                render: ({ row }) => text(row.owner?.institutionName)
            },
            {
                key: "device",
                label: "摄像头",
                minWidth: 148,
                prop: "device.createdTime",
                sortable: true,
                render: ({ row }) => {
                    const device = firstDevice(row.device);
                    return device ? (
                        <span>
                            <span aria-label={device.online === 1 ? "设备在线" : "设备离线"}>
                                {device.online === 1 ? "●" : "○"}
                            </span>{" "}
                            {text(device.name || "摄像头")}
                        </span>
                    ) : (
                        "--"
                    );
                }
            },
            {
                key: "captain",
                label: "船主",
                width: 106,
                prop: "captain.realName",
                render: ({ row }) => (row.ownerShip.state === 4 ? text(row.captain?.realName) : "--")
            },
            {
                key: "partnership",
                label: "合作关系",
                width: 154,
                prop: "ownerShip.partnership",
                render: ({ row }) => PARTNERSHIP.getIntroByCode(row.ownerShip.partnership) || "--"
            },
            {
                key: "state",
                label: "认证状态",
                width: 106,
                prop: "ownerShip.state",
                render: ({ row }) => <Tag>{label(row.ownerShip.state)}</Tag>
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: 154,
                prop: "ownerShip.createdTime",
                render: ({ row }) => dateText(row.ownerShip.createdTime)
            },
            {
                key: "operation",
                label: "操作",
                width: 365,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        {mayManage && row.ownerShip.state === 2 ? (
                            <Button disabled={isButtonBanned} type="link" onClick={() => setAuditRow(row)}>
                                审核
                            </Button>
                        ) : null}
                        {mayManage && row.ownerShip.state === 4 ? (
                            <Button disabled={isButtonBanned} type="link" onClick={() => setUnbindRow(row)}>
                                解绑
                            </Button>
                        ) : null}
                        {row.ownerShip.state === 4 ? (
                            <Button disabled={isButtonBanned} type="link" onClick={() => void handleMonitor(row)}>
                                监控
                            </Button>
                        ) : null}
                    </Space>
                )
            }
        ],
        [handleMonitor, isButtonBanned, label, mayManage]
    );

    return (
        <section aria-label="transport-capacity-management-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space wrap>
                    <Input
                        aria-label="船舶名称或MMSI"
                        placeholder="请输入船名关键词或MMSI搜索"
                        value={shipKeywords}
                        onChange={event => setShipKeywords(event.target.value)}
                    />
                    <Input
                        aria-label="货主名称"
                        placeholder="请输入货主名称搜索"
                        value={ownerKeywords}
                        onChange={event => setOwnerKeywords(event.target.value)}
                    />
                    <Select
                        aria-label="认证状态"
                        allowClear
                        placeholder="请选择认证状态"
                        value={state}
                        options={TRANSPORT_CAPACITY_STATE.options.filter(option => option.value !== 1)}
                        onChange={setState}
                    />
                    <Select
                        aria-label="合作关系"
                        allowClear
                        placeholder="请选择合作关系"
                        value={partnership}
                        options={PARTNERSHIP.options}
                        onChange={setPartnership}
                    />
                    <Button aria-label="查询" type="primary" onClick={submit}>
                        查询
                    </Button>
                    <Button
                        aria-label="重置"
                        onClick={() => {
                            setShipKeywords("");
                            setOwnerKeywords("");
                            setState(undefined);
                            setPartnership(undefined);
                            setQuery({ page: 1, size: 10 });
                        }}
                    >
                        重置
                    </Button>
                </Space>
            </Card>
            <Card>
                <ZhTable<TransportCapacityRow>
                    rowKey={row => String(row.ownerShip.id)}
                    data={rows}
                    columns={normalizeOperationColumns(capacityColumns)}
                    emptyText="暂无运力"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="w-full" align="center">
                                <Typography.Title level={4} className="mb-0">
                                    运力管理
                                </Typography.Title>
                                {mayManage ? (
                                    <Link to="/logistics/transportCapacityManagement/capacityManagement/addCapacity">
                                        <Button type="primary">新增船舶</Button>
                                    </Link>
                                ) : null}
                            </Space>

                            {errorMessage ? <Alert role="alert" type="error" message={errorMessage} /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            {auditRow ? (
                <ZhDialog
                    open
                    title="运力审核"
                    width={520}
                    footer={null}
                    onCancel={() => setAuditRow(undefined)}
                    destroyOnHidden
                >
                    <TransportCapacityReview
                        capacity={auditRow}
                        validateAudit={validateAudit}
                        auditCapacity={async (id, payload) => {
                            if (auditCapacity) await auditCapacity(id, payload);
                            setAuditRow(undefined);
                            refresh();
                        }}
                        canManage={mayManage}
                    />
                </ZhDialog>
            ) : null}
            <ZhDialog
                open={Boolean(unbindRow)}
                title="解除绑定"
                width={420}
                onCancel={() => setUnbindRow(undefined)}
                footer={
                    <Space>
                        <Button onClick={() => setUnbindRow(undefined)}>取消</Button>
                        <Button type="primary" onClick={confirmUnbind}>
                            确认解绑
                        </Button>
                    </Space>
                }
            >
                <ZhMultiGrid>
                    <ZhGrid cols={1} gap={[16, 16]}>
                        <ZhBaseItem label="货主" value={text(unbindRow?.owner?.institutionName)} />
                        <ZhBaseItem label="船舶" value={text(unbindRow?.ship?.cnname || unbindRow?.ship?.name)} />
                        <ZhBaseItem
                            label="合作关系"
                            value={PARTNERSHIP.getIntroByCode(unbindRow?.ownerShip?.partnership) || "--"}
                        />
                    </ZhGrid>
                </ZhMultiGrid>
            </ZhDialog>
            <ShipMonitorModal
                open={monitorOpen}
                device={monitorDevice}
                shipId={monitorShipId}
                ship={monitorShip?.ship}
                onCancel={() => setMonitorOpen(false)}
            />
        </section>
    );
}

export default TransportCapacityManagementPage;
